// KYB field validators — regex + logical completeness checks.
// Kept in a plain JS module so it's easy to unit-test and reuse.

// Format regexes. LLPIN intentionally omitted here — we don't have a
// verified real-example pattern, so we apply only a loose length check.
export const RE = {
  PAN:   /^[A-Z]{5}\d{4}[A-Z]{1}$/,
  GSTIN: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  CIN:   /^[UL][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
  IFSC:  /^[A-Z]{4}0[A-Z0-9]{6}$/,
};

// Loose LLPIN check: 5-15 chars, alphanumeric + hyphen. Real MCA LLPINs
// vary in shape (e.g. "AAA-1234"); this avoids rejecting valid values.
export const isLooseLlpin = (v) =>
  typeof v === "string" && v.length >= 5 && v.length <= 15 && /^[A-Z0-9-]+$/i.test(v);

// Per-field validators. Return null if OK, else a short human message.
export const check = {
  pan:   (v) => !v ? null : (RE.PAN.test(v)   ? null : "PAN must be like ABCDE1234F"),
  gstin: (v) => !v ? null : (RE.GSTIN.test(v) ? null : "GSTIN must be like 22ABCDE1234F1Z5"),
  cin:   (v) => !v ? null : (RE.CIN.test(v)   ? null : "CIN must be like U12345KL2020PTC062345"),
  ifsc:  (v) => !v ? null : (RE.IFSC.test(v)  ? null : "IFSC must be like HDFC0000261"),
  llpin: (v) => !v ? null : (isLooseLlpin(v)  ? null : "LLPIN looks invalid (letters, digits, hyphens)"),
};

// Required role gating per entity type (matches services/kyb_service.py).
const REQUIRED_ROLES = {
  sole_proprietor: [],
  partnership: ["beneficial_owner", "designated_partner", "representative"],
  llp:         ["beneficial_owner", "designated_partner", "representative"],
  company:     ["beneficial_owner", "director", "representative"],
};

const ROLE_LABEL = {
  beneficial_owner: "Beneficial Owner",
  director: "Business Director",
  designated_partner: "Designated Partner",
  representative: "Business Representative",
};

/**
 * Pre-flight completeness check — runs client-side before /kyb/submit.
 * Mirrors services/kyb_service.py:validate_kyb so a submit that reaches
 * the server should never come back 422. Returns a list of {code, message}
 * that the wizard renders as a checklist.
 *
 * The same person in multiple roles is valid — we do NOT de-dupe by PAN.
 * BO percent totals are NOT enforced (real cap tables get messy).
 */
export function preflightCheck(kyb) {
  const issues = [];
  const push = (message, code) => issues.push({ code, message });

  if (!kyb || !kyb.entity_type) {
    push("Select the entity type on step 1.", "entity_type");
    return issues;
  }
  const et = kyb.entity_type;
  const g = kyb.generic || {};
  const ids = kyb.business_ids || {};
  const persons = kyb.persons || [];

  // Generic
  if (!g.legal_name) push("Legal name is required.", "legal_name");
  if (!g.business_description) push("Business description is required.", "business_description");
  if (!g.website && !g.no_website) push("Enter a website or tick 'No website available'.", "website");
  const addr = g.physical_address || {};
  for (const k of ["line1", "city", "state", "postal_code"]) {
    if (!addr[k]) push(`Address ${k.replace("_", " ")} is required.`, `addr_${k}`);
  }

  // Per-type IDs + regex checks
  if (et === "sole_proprietor") {
    if (!ids.pan && !ids.pan_file_id) push("PAN (number or PAN card upload) is required.", "pan");
    if (ids.pan && check.pan(ids.pan)) push(check.pan(ids.pan), "pan_format");
    const hasGst = ids.gstin || ids.gstin_file_id;
    if (!hasGst && !ids.permanent_address) {
      push("Provide either GSTIN or your permanent address.", "gstin_or_addr");
    }
    if (ids.gstin && check.gstin(ids.gstin)) push(check.gstin(ids.gstin), "gstin_format");
  } else if (et === "partnership") {
    if (!ids.gstin && !ids.gstin_file_id) push("GSTIN (number or certificate) is required.", "gstin");
    if (ids.gstin && check.gstin(ids.gstin)) push(check.gstin(ids.gstin), "gstin_format");
    if (!ids.partnership_deed_file_id) push("Partnership deed upload is required.", "deed");
  } else {
    // llp / company
    const t = ids.primary_id_type;
    const num = ids.primary_id_number;
    if (!t || (!num && !ids.primary_id_file_id)) {
      push("Choose a primary business ID and provide a number or upload the certificate.", "primary_id");
    } else if (num) {
      if (t === "cin"   && check.cin(num))   push(check.cin(num),   "cin_format");
      if (t === "gstin" && check.gstin(num)) push(check.gstin(num), "gstin_format");
      if (t === "pan"   && check.pan(num))   push(check.pan(num),   "pan_format");
      if (t === "llpin" && check.llpin(num)) push(check.llpin(num), "llpin_format");
    }
  }

  // Required roles present (≥1 of each)
  for (const role of REQUIRED_ROLES[et] || []) {
    if (!persons.some((p) => p.role === role)) {
      push(`At least one ${ROLE_LABEL[role]} is required.`, `role_${role}`);
    }
  }

  // Each person: name, doc, and PAN format if provided
  persons.forEach((p, i) => {
    if (!p.first_name || !p.last_name) {
      push(`${ROLE_LABEL[p.role] || "Person"} #${i + 1}: first + last name required.`, `person_${i}_name`);
    }
    if (!p.pan_number && !p.id_document_file_id) {
      push(`${ROLE_LABEL[p.role] || "Person"} #${i + 1}: upload PAN or Passport.`, `person_${i}_doc`);
    }
    if (p.pan_number && check.pan(p.pan_number)) {
      push(`${ROLE_LABEL[p.role] || "Person"} #${i + 1}: ${check.pan(p.pan_number)}`, `person_${i}_pan_format`);
    }
  });

  return issues;
}
